import type { RegisteredComponent } from "@builder.io/sdk-react";

import { Button } from "~/components/ui/button";
import { sanitizeHtml } from "~/utils/sanitize-html";

import { ContentCard } from "./content-card";
import type {
  ContentCard as ContentCardType,
  ContentCardsProps,
} from "./types";

const ContentCards = (props: ContentCardsProps) => {
  const cards = props.contentCard || [];
  const totalCards = cards.length;
  const urlLink = props.external
    ? (props.external_link?.value?.data?.url ?? "#")
    : (props.link?.value?.data?.url ?? "#");

  return (
    <div
      className="mb-4 mt-4 flex flex-col gap-y-4"
      data-testid="content-cards-component"
    >
      {props.heading && (
        <div className="content-cards-title">
          <div
            className="min-h-[43px] text-center text-2xl font-medium lg:text-3xl"
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(props.heading),
            }}
          />
        </div>
      )}
      <div className="mx-auto grid max-w-[349px] grid-cols-2 gap-[9px] sm:max-w-none sm:grid-cols-3 xl:gap-[24px]">
        {cards.map((card: ContentCardType, index: number) => (
          <ContentCard
            key={index}
            {...card}
            index={index}
            totalCards={totalCards}
          />
        ))}
      </div>
      <div className="mx-auto mt-4 flex max-w-[1236px] flex-col items-center justify-start gap-y-4 sm:items-start lg:mx-auto lg:flex-row lg:items-center lg:justify-center">
        {props.text && (
          <div
            className="flex w-full max-w-[340px] items-center justify-center text-left sm:max-w-[705px] xl:max-w-[75%]"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(props.text) }}
          />
        )}
        <div className="flex w-full items-start justify-center sm:items-start sm:justify-start lg:max-w-[25%] lg:items-center lg:justify-center">
          {props.ctaText && (
            <Button
              as="a"
              href={urlLink}
              className="px-6 py-3 text-white"
              style={
                props.buttonColor
                  ? { backgroundColor: props.buttonColor }
                  : undefined
              }
              data-testid="content-cards-cta"
            >
              {props.ctaText}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const ContentCardsDefinition: RegisteredComponent = {
  component: ContentCards,
  name: "contentCards",
  inputs: [
    {
      name: "heading",
      type: "richText",
      defaultValue: "Heading",
      helperText: "The heading for the content cards section.",
    },
    {
      name: "text",
      type: "richText",
      defaultValue: "This is a sample text for the content cards section.",
      helperText: "The text for the content cards section.",
    },
    {
      name: "ctaText",
      type: "string",
      defaultValue: "Shop Now",
      helperText: "The call to action text for the content cards section.",
    },
    {
      name: "buttonColor",
      type: "color",
      helperText: "The button color for the call to action button.",
    },
    {
      name: "external",
      type: "boolean",
      defaultValue: false,
      helperText: "Set to true if the link is external.",
    },
    {
      name: "external_link",
      type: "reference",
      model: "external-links",
      showIf: `options.get('external') === true`,
      helperText: "The external link for the call to action button.",
    },
    {
      name: "link",
      type: "reference",
      model: "page",
      showIf: `options.get('external') === false`,
      helperText: "The internal link for the call to action button.",
    },
    {
      name: "contentCard",
      type: "list",
      subFields: [
        {
          name: "overlayImage",
          type: "file",
          helperText: "The overlay image for the card.",
        },
        {
          name: "overlayImagePosition",
          type: "string",
          enum: [
            "top",
            "top-left",
            "top-right",
            "center",
            "center-left",
            "center-right",
            "bottom",
            "bottom-left",
            "bottom-right",
          ],
          defaultValue: "center",
          helperText: "The position of the overlay image.",
        },
        {
          name: "backgroundImageType",
          type: "string",
          enum: ["color", "image"],
          defaultValue: "color",
          helperText: "The type of background for the card.",
        },
        {
          name: "backgroundColor",
          type: "string",
          showIf: `options.get('backgroundImageType') === 'color'`,
          defaultValue: "#FFFFFF",
          helperText: "The background color for the card.",
        },
        {
          name: "backgroundImage",
          type: "file",
          showIf: `options.get('backgroundImageType') === 'image'`,
          helperText: "The background image for the card.",
        },
        {
          name: "external",
          type: "boolean",
          defaultValue: false,
          helperText: "Set to true if the link is external.",
        },
        {
          name: "external_link",
          type: "reference",
          model: "external-links",
          showIf: `options.get('external') === true`,
          helperText: "The external link for the column item.",
        },
        {
          name: "link",
          type: "reference",
          model: "page",
          showIf: `options.get('external') === false`,
          helperText: "The internal link for the column item.",
        },
        {
          name: "text",
          type: "longText",
          helperText: "The text for the column item.",
        },
        {
          name: "ctaText",
          type: "text",
          helperText: "The call to action text for the column item.",
        },
      ],
      helperText: "The array of items to display in the category columns.",
    },
  ],
};

export default ContentCards;
